import React from "react";
import { Link } from "react-router-dom";
import { useSettings } from "@/context/SettingsContext";
import { BrandMark } from "@/components/BrandLogo";
import { useEnquiry } from "@/context/EnquiryContext";
import { Mail, Phone, MapPin, Clock, Instagram, Facebook, Youtube, Twitter, Linkedin, MessageCircle } from "lucide-react";

function waNumber(wa) {
  if (!wa) return "";
  return wa.replace(/[^0-9]/g, "");
}

export default function Footer() {
  const { settings: s } = useSettings();
  const { openEnquiry } = useEnquiry();
  const addr = [s.address_line1, s.address_line2, s.city, s.state, s.pincode, s.country].filter(Boolean).join(", ");
  const socials = [
    { url: s.instagram_url, icon: Instagram, label: "Instagram" },
    { url: s.facebook_url, icon: Facebook, label: "Facebook" },
    { url: s.youtube_url, icon: Youtube, label: "YouTube" },
    { url: s.twitter_url, icon: Twitter, label: "Twitter / X" },
    { url: s.linkedin_url, icon: Linkedin, label: "LinkedIn" },
  ].filter((x) => x.url);

  return (
    <footer className="mt-16 border-t border-border bg-[hsl(var(--brand-navy))] text-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-4">
        <div className="lg:col-span-1">
          <div className="flex items-center gap-2">
            <BrandMark className="h-10 w-10 bg-white rounded-md p-1" />
            <div className="font-display text-lg font-black">{s.site_name}</div>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-white/75">
            {s.footer_about || "PilotYatra helps aspiring Indian pilots prepare for DGCA examinations."}
          </p>
          {socials.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {socials.map((so) => (
                <a
                  key={so.label}
                  href={so.url}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={so.label}
                  className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 transition hover:bg-[hsl(var(--brand-orange))]"
                >
                  <so.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="font-display text-sm font-bold uppercase tracking-wider text-[hsl(var(--brand-orange))]">Explore</div>
          <ul className="mt-4 space-y-2 text-sm text-white/80">
            <li><Link className="hover:text-white" to="/">Home</Link></li>
            <li><Link className="hover:text-white" to="/courses">Courses</Link></li>
            <li><button type="button" className="hover:text-white" onClick={() => openEnquiry()} data-testid="footer-enquire-link">Enquire Now</button></li>
            <li><Link className="hover:text-white" to="/contact">Contact</Link></li>
            <li><Link className="hover:text-white" to="/login">Student Login</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-display text-sm font-bold uppercase tracking-wider text-[hsl(var(--brand-orange))]">Contact</div>
          <ul className="mt-4 space-y-3 text-sm text-white/80">
            {s.phone_primary && (
              <li className="flex items-start gap-2">
                <Phone className="mt-0.5 h-4 w-4 flex-none text-white/60" />
                <div>
                  <a href={`tel:${s.phone_primary}`} className="hover:text-white">{s.phone_primary}</a>
                  {s.phone_secondary && <div><a className="hover:text-white" href={`tel:${s.phone_secondary}`}>{s.phone_secondary}</a></div>}
                </div>
              </li>
            )}
            {s.whatsapp && (
              <li className="flex items-start gap-2">
                <MessageCircle className="mt-0.5 h-4 w-4 flex-none text-white/60" />
                <a href={`https://wa.me/${waNumber(s.whatsapp)}`} target="_blank" rel="noreferrer" className="hover:text-white">WhatsApp {s.whatsapp}</a>
              </li>
            )}
            {s.email && (
              <li className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 flex-none text-white/60" />
                <a href={`mailto:${s.email}`} className="hover:text-white">{s.email}</a>
              </li>
            )}
            {s.business_hours && (
              <li className="flex items-start gap-2">
                <Clock className="mt-0.5 h-4 w-4 flex-none text-white/60" />
                <span>{s.business_hours}</span>
              </li>
            )}
          </ul>
        </div>

        <div>
          <div className="font-display text-sm font-bold uppercase tracking-wider text-[hsl(var(--brand-orange))]">Visit</div>
          <ul className="mt-4 space-y-3 text-sm text-white/80">
            {addr && (
              <li className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 flex-none text-white/60" />
                <span>{addr}</span>
              </li>
            )}
            {s.google_maps_url && (
              <li>
                <a href={s.google_maps_url} target="_blank" rel="noreferrer" className="text-[hsl(var(--brand-orange))] hover:underline">Open in Google Maps →</a>
              </li>
            )}
            {s.support_email && s.support_email !== s.email && (
              <li className="flex items-start gap-2">
                <Mail className="mt-0.5 h-4 w-4 flex-none text-white/60" />
                <a href={`mailto:${s.support_email}`} className="hover:text-white">Support: {s.support_email}</a>
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-white/60">
        © {new Date().getFullYear()} {s.academy_name || s.site_name}. All rights reserved.
      </div>
    </footer>
  );
}
